import { useState, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { startOfWeek, endOfWeek, format } from 'date-fns';
import { formatDistanceToNow } from 'date-fns';
import { useWeeklyPlan, getWeekId } from '@/hooks/useWeeklyPlan';
import { useIsMobile } from '@/hooks/useIsMobile';
import { DAY_THEME_ICONS, DayTheme, Capture, CaptureType } from '@/types';
import styles from './RightSidebar.module.css';

type TabType = 'weekly' | 'inbox';

interface RightSidebarProps {
  selectedDate: Date;
  // Firestore-backed inbox
  inboxCaptures: Capture[];
  onCreateCapture: (content: string) => void;
  onArchiveCapture: (captureId: string) => void;
  onDeleteCapture: (captureId: string) => void;
  onConvertToTask: (capture: Capture) => void;
  onConvertToBlock: (capture: Capture) => void;
}

const TYPE_ICONS: Record<CaptureType, string> = {
  thought: '\u{1F4AD}',
  task: '\u{2705}',
  tomorrow: '\u{1F4C5}',
  soon: '\u{1F51C}',
  note: '\u{1F4DD}',
  'quick-block': '\u{26A1}',
};

export function RightSidebar({
  selectedDate,
  inboxCaptures,
  onCreateCapture,
  onArchiveCapture,
  onDeleteCapture,
  onConvertToTask,
  onConvertToBlock,
}: RightSidebarProps) {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [activeTab, setActiveTab] = useState<TabType>('weekly');
  const [isCollapsed, setIsCollapsed] = useState(isMobile);

  // Collapse sidebar when switching to mobile
  useEffect(() => {
    if (isMobile) {
      setIsCollapsed(true);
    }
  }, [isMobile]);

  // Weekly state
  const [newGoalTitle, setNewGoalTitle] = useState('');
  const weekStart = startOfWeek(selectedDate, { weekStartsOn: 1 });
  const weekEnd = endOfWeek(selectedDate, { weekStartsOn: 1 });
  const weekId = getWeekId(weekStart);
  const {
    weeklyPlan,
    isLoading: weeklyLoading,
    addGoal,
    toggleGoalCompletion,
    removeGoal,
  } = useWeeklyPlan({ weekId });

  const goals = weeklyPlan?.goals || [];
  const dayThemes = weeklyPlan?.dayThemes || {};
  const completedGoals = goals.filter((g) => g.completed).length;

  // Inbox state
  const [newItemText, setNewItemText] = useState('');

  // Weekly handlers
  const handleAddGoal = useCallback(async () => {
    if (newGoalTitle.trim() && goals.length < 5) {
      const success = await addGoal(newGoalTitle.trim());
      if (success) {
        setNewGoalTitle('');
      }
    }
  }, [newGoalTitle, goals.length, addGoal]);

  // Inbox handlers
  const handleAddInboxItem = useCallback(async () => {
    if (!newItemText.trim()) return;
    try {
      await onCreateCapture(newItemText.trim());
      setNewItemText('');
    } catch {
      // Input preserved on failure so user can retry
    }
  }, [newItemText, onCreateCapture]);

  // On mobile, show floating toggle button when collapsed
  if (isMobile && isCollapsed) {
    return (
      <motion.button
        className={styles.mobileToggle}
        onClick={() => setIsCollapsed(false)}
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        whileTap={{ scale: 0.95 }}
        aria-label="Open sidebar"
      >
        <span className={styles.mobileToggleIcon}>📋</span>
        {(goals.length > 0 || inboxCaptures.length > 0) && (
          <span className={styles.mobileToggleBadge}>{goals.length + inboxCaptures.length}</span>
        )}
      </motion.button>
    );
  }

  return (
    <>
      {/* Mobile backdrop */}
      {isMobile && !isCollapsed && (
        <motion.div
          className={styles.backdrop}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={() => setIsCollapsed(true)}
        />
      )}

      <aside className={`${styles.sidebar} ${isMobile ? styles.mobileSidebar : ''}`}>
        {/* Header with Tabs */}
        <div className={styles.header}>
          <div className={styles.tabs}>
            <button
              className={`${styles.tab} ${activeTab === 'weekly' ? styles.activeTab : ''}`}
              onClick={() => setActiveTab('weekly')}
            >
              📋 Weekly
              {goals.length > 0 && (
                <span className={styles.tabBadge}>
                  {completedGoals}/{goals.length}
                </span>
              )}
            </button>
            <button
              className={`${styles.tab} ${activeTab === 'inbox' ? styles.activeTab : ''}`}
              onClick={() => setActiveTab('inbox')}
            >
              📥 Inbox
              {inboxCaptures.length > 0 && (
                <span className={styles.tabBadge}>{inboxCaptures.length}</span>
              )}
            </button>
          </div>
          {isMobile && (
            <button
              className={styles.closeButton}
              onClick={() => setIsCollapsed(true)}
              aria-label="Close sidebar"
            >
              ×
            </button>
          )}
        </div>

        {/* Tab Content */}
        <div className={styles.content}>
          <AnimatePresence mode="wait">
            {activeTab === 'weekly' ? (
              <motion.div
                key="weekly"
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 10 }}
                className={styles.tabContent}
              >
                {/* Week Range */}
                <div className={styles.weekRange}>
                  {format(weekStart, 'MMM d')} - {format(weekEnd, 'MMM d, yyyy')}
                </div>

                {/* Goals */}
                <div className={styles.section}>
                  <div className={styles.sectionHeader}>
                    <span>Goals</span>
                  </div>

                  {weeklyLoading ? (
                    <div className={styles.loading}>Loading...</div>
                  ) : (
                    <>
                      <div className={styles.list}>
                        {goals.map((goal) => (
                          <div
                            key={goal.id}
                            className={`${styles.item} ${goal.completed ? styles.completed : ''}`}
                          >
                            <button
                              className={`${styles.checkbox} ${goal.completed ? styles.checked : ''}`}
                              onClick={() => toggleGoalCompletion(goal.id)}
                            >
                              {goal.completed && '✓'}
                            </button>
                            <span className={styles.itemText}>{goal.title}</span>
                            <button
                              className={styles.deleteBtn}
                              onClick={() => removeGoal(goal.id)}
                            >
                              ×
                            </button>
                          </div>
                        ))}
                      </div>

                      {goals.length < 5 && (
                        <div className={styles.addRow}>
                          <input
                            type="text"
                            placeholder="Add goal..."
                            value={newGoalTitle}
                            onChange={(e) => setNewGoalTitle(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && handleAddGoal()}
                            className={styles.input}
                          />
                          <button
                            className={styles.addBtn}
                            onClick={handleAddGoal}
                            disabled={!newGoalTitle.trim()}
                          >
                            +
                          </button>
                        </div>
                      )}

                      {goals.length === 0 && (
                        <p className={styles.emptyHint}>Set 3-5 goals for this week</p>
                      )}
                    </>
                  )}
                </div>

                {/* Day Themes */}
                {Object.entries(dayThemes).some(([_, t]) => t !== null) && (
                  <div className={styles.section}>
                    <div className={styles.sectionHeader}>
                      <span>Day Themes</span>
                    </div>
                    <div className={styles.themesList}>
                      {Object.entries(dayThemes)
                        .filter(
                          (entry): entry is [string, NonNullable<DayTheme>] => entry[1] !== null
                        )
                        .map(([dateKey, theme]) => (
                          <div key={dateKey} className={styles.themeItem}>
                            <span className={styles.themeDay}>
                              {format(new Date(dateKey), 'EEE')}
                            </span>
                            <span>{DAY_THEME_ICONS[theme]}</span>
                            <span className={styles.themeName}>{theme}</span>
                          </div>
                        ))}
                    </div>
                  </div>
                )}

                {/* Full View Link */}
                <motion.button
                  className={styles.fullViewBtn}
                  onClick={() => navigate('/weekly')}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Open Full Weekly View
                </motion.button>
              </motion.div>
            ) : (
              <motion.div
                key="inbox"
                initial={{ opacity: 0, x: 10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -10 }}
                className={styles.tabContent}
              >
                {/* Quick Capture */}
                <div className={styles.addRow}>
                  <input
                    type="text"
                    placeholder="Capture a thought..."
                    value={newItemText}
                    onChange={(e) => setNewItemText(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleAddInboxItem()}
                    className={styles.input}
                  />
                  <button
                    className={styles.addBtn}
                    onClick={handleAddInboxItem}
                    disabled={!newItemText.trim()}
                  >
                    +
                  </button>
                </div>

                {/* Inbox Items */}
                <div className={styles.list}>
                  {inboxCaptures.map((capture) => (
                    <div key={capture.id} className={styles.item}>
                      <span className={styles.typeIcon} title={capture.type}>
                        {TYPE_ICONS[capture.type]}
                      </span>
                      <div className={styles.itemContent}>
                        <span className={styles.itemText}>{capture.content}</span>
                        <span className={styles.itemTime}>
                          {formatDistanceToNow(capture.createdAt, { addSuffix: true })}
                        </span>
                      </div>
                      <div className={styles.itemActions}>
                        <button
                          className={styles.actionBtn}
                          onClick={() => onConvertToTask(capture)}
                          title="Convert to Task"
                        >
                          📋
                        </button>
                        <button
                          className={styles.actionBtn}
                          onClick={() => onConvertToBlock(capture)}
                          title="Convert to Block"
                        >
                          📅
                        </button>
                        <button
                          className={styles.archiveBtn}
                          onClick={() => onArchiveCapture(capture.id)}
                          title="Archive"
                        >
                          📦
                        </button>
                        <button
                          className={styles.deleteBtn}
                          onClick={() => onDeleteCapture(capture.id)}
                          title="Delete"
                        >
                          ×
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                {inboxCaptures.length === 0 && (
                  <div className={styles.emptyState}>
                    <p>Your inbox is empty</p>
                    <span>Capture thoughts, ideas, and tasks here</span>
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </aside>
    </>
  );
}
